import React, { useEffect, useState } from 'react';
import { toFlag } from 'pano-utils';
import { useGate, useStore } from 'effector-react';
import {
  type EditConfigCustomIcons,
  type GlobalFlags,
  ImageType,
  MediaType,
  TemplateId,
} from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import useTourEditing from '@hooks/useTourEditing';
import FeatureToggle from '@components/FeatureToggle';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Tabbar from '@components/Tabbar';
import {
  $editingFlag,
  $GateEditingCustomIcons,
  $GateEditingLikeBtnActive,
} from '@stores/editingStores';
import {
  customIconDefaultMap,
  customIconLegacyDefaultMap,
} from '@modules/player/constants';
import { $templateId } from '@stores/dataStores';
import type { PanelBaseProps } from '../types';
import PanelBase from '../PanelBase';
import { selectLibCBIcons } from '../../mediaLib';

export const flagDefaultInteractive: GlobalFlags = {
  showShareBtn: 1,
  showLikeBtn: 1,
  showCommentBtnV2: 1,
  showComment: 1,
  checkCommentByAuthor: 0,
};

export const flagKeysInteractive = Object.keys(
  flagDefaultInteractive
) as (keyof GlobalFlags)[];

export const customIconDefaultInteractive: EditConfigCustomIcons = {
  likeBtn: {},
  likeBtnActive: {},
  commentBtn: {},
  shareBtn: {},
};

export const customIconKeysInteractive = Object.keys(
  customIconDefaultInteractive
) as (keyof EditConfigCustomIcons)[];

export function InteractiveBase({
  defaultValue = {},
  onConfirm,
}: PanelBaseProps<{
  flag?: GlobalFlags;
  customIcons?: EditConfigCustomIcons;
}>) {
  const { flag: defaultFlag, customIcons: defaultCustomIcons } = defaultValue;
  const flag = useTourEditing(defaultFlag, flagDefaultInteractive);
  const customIcons = useTourEditing(
    defaultCustomIcons,
    customIconDefaultInteractive
  );
  const [likeTab, setLikeTab] = useState(0);
  const templateId = useStore($templateId);
  const showIconSetting =
    templateId === TemplateId.free ||
    templateId === TemplateId.vip ||
    templateId === TemplateId.legacy;

  useEffect(() => {
    $editingFlag.api.set(flag.state);
    return () => {
      $editingFlag.api.set(null);
    };
  }, [flag.state]);

  useGate($GateEditingCustomIcons, customIcons.state);
  useGate($GateEditingLikeBtnActive, likeTab === 1);

  const getToggleProps = (key: keyof GlobalFlags) => {
    return {
      isOn: !!flag.state[key],
      onToggle: (bool: boolean) => {
        flag.setState((draft) => {
          draft[key] = toFlag(bool);
        });
      },
    };
  };

  const handleIconSelect = (key: keyof EditConfigCustomIcons) => async () => {
    const { libItems, sysItems } = await selectLibCBIcons();
    if (sysItems) {
      customIcons.setState((draft) => {
        draft[key] = {
          type: MediaType.system,
          mediaId: 0,
          mediaUrl: sysItems[0].url,
        };
      });
    } else if (libItems) {
      customIcons.setState((draft) => {
        draft[key] = {
          type: MediaType.lib,
          mediaId: libItems[0].id,
          mediaUrl: libItems[0].url,
        };
      });
    }
  };

  const renderIconSetting = (key: keyof EditConfigCustomIcons) => {
    const icon = customIcons.state[key];
    const iconMap =
      templateId === TemplateId.legacy
        ? customIconLegacyDefaultMap
        : customIconDefaultMap;
    return (
      <>
        <ImageContainer
          className="mt-20 h-70"
          useResetIcon
          onDeleteClick={() => {
            customIcons.setState((draft) => {
              delete draft[key];
            });
          }}
          placeholder={
            <Image
              className="h-40 w-40"
              src={iconMap[key]}
              type={ImageType.sysMedia}
            />
          }
        >
          {!!icon?.mediaUrl && (
            <Image
              src={icon.mediaUrl}
              type={
                icon?.type === MediaType.lib
                  ? ImageType.media
                  : ImageType.sysMedia
              }
              className="h-40 w-40"
            />
          )}
        </ImageContainer>
        <div className="mt-20 flex items-center justify-between">
          <Tip>建议尺寸：80 X 80</Tip>
          <Button size="sm" onClick={handleIconSelect(key)}>
            编辑图标
          </Button>
        </div>
      </>
    );
  };

  return (
    <PanelBase
      title={<Title>互动功能</Title>}
      onConfirm={() => {
        onConfirm({
          flag: flag.state,
          customIcons: customIcons.state,
        });
      }}
    >
      <Collapse title="分享设置">
        <FeatureToggle title="启用功能" {...getToggleProps('showShareBtn')} />
        {!!flag.state.showShareBtn &&
          showIconSetting &&
          renderIconSetting('shareBtn')}
      </Collapse>
      <Collapse title="点赞设置">
        <FeatureToggle title="启用功能" {...getToggleProps('showLikeBtn')} />
        {!!flag.state.showLikeBtn && showIconSetting && (
          <>
            <Tabbar
              activeIndex={likeTab}
              tabs={['未点赞状态', '已点赞状态']}
              className="mt-20"
              onChange={(index) => {
                setLikeTab(index);
              }}
            />
            {likeTab === 0 && renderIconSetting('likeBtn')}
            {likeTab === 1 && renderIconSetting('likeBtnActive')}
          </>
        )}
      </Collapse>
      <Collapse title="留言设置">
        <FeatureToggle
          title="启用功能"
          {...getToggleProps('showCommentBtnV2')}
        />
        {!!flag.state.showCommentBtnV2 && (
          <>
            {showIconSetting && renderIconSetting('commentBtn')}
            <FeatureToggle
              title="场景中默认显示留言内容"
              {...getToggleProps('showComment')}
            />
            <FeatureToggle
              title="留言审核"
              {...getToggleProps('checkCommentByAuthor')}
            />
            <Tip className="mt-10">
              系统已进行自动审核，开启后需要创作者二次手动审核后方可显示。
            </Tip>
          </>
        )}
      </Collapse>
    </PanelBase>
  );
}
